import { useCallback, useEffect, useState } from "react";
import api, { apiError, localApi } from "@/lib/api";
import { PageHeader, Loading, EmptyState, ConfirmDialog } from "@/components/common";
import { fmtMoney, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash, Eye, CircleNotch, StackPlus, Calculator, PaperPlaneRight, FilePdf, PencilSimple, CaretDown, CaretRight } from "@phosphor-icons/react";
import { format } from "date-fns";

const inputCls = "rounded-sm border-input bg-background text-foreground focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-0";
const PERIODS = [
  { value: "weekly", label: "Semanal" },
  { value: "biweekly", label: "Quincenal" },
  { value: "monthly", label: "Mensual" },
];
const periodLabel = (v) => PERIODS.find((p) => p.value === v)?.label || v;

function suggestPeriod(type) {
  const now = new Date();
  const y = now.getFullYear(), m = now.getMonth();
  let s, e;
  if (type === "monthly") {
    s = new Date(y, m, 1); e = new Date(y, m + 1, 0);
  } else if (type === "biweekly") {
    if (now.getDate() <= 15) { s = new Date(y, m, 1); e = new Date(y, m, 15); }
    else { s = new Date(y, m, 16); e = new Date(y, m + 1, 0); }
  } else {
    const day = (now.getDay() + 6) % 7;
    s = new Date(now); s.setDate(now.getDate() - day);
    e = new Date(s); e.setDate(s.getDate() + 6);
  }
  return { period_start: format(s, "yyyy-MM-dd"), period_end: format(e, "yyyy-MM-dd") };
}

const EMPTY = {
  employee_id: "", period_type: "biweekly", period_start: "", period_end: "",
  days_worked: "", overtime_hours: "", bonuses: "", deductions: "",
  apply_auto_deductions: true, notes: "",
};

export default function Planillas() {
  const [payrolls, setPayrolls] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [batchOpen, setBatchOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [batchForm, setBatchForm] = useState({ period_type: "biweekly", period_start: "", period_end: "", apply_auto_deductions: true, items: [] });
  const [toolMode, setToolMode] = useState(null); // "vouchers" | "pdf"
  const [selectedIds, setSelectedIds] = useState([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sendingVouchers, setSendingVouchers] = useState(false);
  const [voucherResult, setVoucherResult] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [extras, setExtras] = useState({ loans: [], commissions: [] });
  const [selLoans, setSelLoans] = useState({});
  const [selComms, setSelComms] = useState({});
  const [batchExtras, setBatchExtras] = useState(true);
  const [collapsed, setCollapsed] = useState({});
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [resending, setResending] = useState("");
  const [autoCalcLoading, setAutoCalcLoading] = useState(false);
  const [autoData, setAutoData] = useState(null);
  const [applyOt, setApplyOt] = useState(true);
  const [applyLate, setApplyLate] = useState(true);
  const [batchAutoOt, setBatchAutoOt] = useState(false);
  const [batchAutoLate, setBatchAutoLate] = useState(false);

  const load = useCallback(() => {
    localApi.get("/payroll-extras/pending").then((r) => setExtras(r.data)).catch(() => {});
    Promise.all([api.get("/payrolls"), api.get("/employees")])
      .then(([p, e]) => {
        setPayrolls(Array.isArray(p.data) ? p.data : []);
        setEmployees((Array.isArray(e.data) ? e.data : []).filter((x) => x.active));
      })
      .catch((err) => {
        toast.error(apiError(err));
        setPayrolls([]);
      });
  }, []);

  useEffect(load, [load]);

  const openCreate = () => {
    const dates = suggestPeriod("biweekly");
    setForm({ ...EMPTY, ...dates });
    setPreview(null);
    setDialogOpen(true);
  };

  const openBatch = () => {
    const dates = suggestPeriod("biweekly");
    setBatchForm({ period_type: "biweekly", ...dates, apply_auto_deductions: true, items: [] });
    setBatchOpen(true);
  };

  const onPeriodType = (v) => setForm((f) => ({ ...f, period_type: v, ...suggestPeriod(v) }));

  const calcPreview = async () => {
    if (!form.employee_id) { toast.error("Selecciona un empleado"); return; }
    try {
      const { data } = await api.post("/payrolls/preview", null, {
        params: { employee_id: form.employee_id, period_type: form.period_type },
      });
      setPreview(data);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const extra = selectedExtras();
      const { data: created } = await api.post("/payrolls", {
        employee_id: form.employee_id,
        period_type: form.period_type,
        period_start: form.period_start,
        period_end: form.period_end,
        days_worked: form.days_worked === "" ? null : Number(form.days_worked),
        overtime_hours: (Number(form.overtime_hours) || 0) + (autoData && applyOt ? autoData.overtime_hours : 0),
        bonuses: (Number(form.bonuses) || 0) + extra.bonTotal,
        deductions: (Number(form.deductions) || 0) + extra.dedTotal + (autoData && applyLate ? autoData.late_deduction : 0),
        apply_auto_deductions: form.apply_auto_deductions,
        notes: form.notes,
        ...(extra.dedTotal > 0 ? { deduction_note: `Préstamo(s): ${fmtMoney(extra.dedTotal)}` } : {}),
        ...(extra.bonTotal > 0 ? { bonus_note: `Comisiones: ${fmtMoney(extra.bonTotal)}` } : {}),
      });
      if (extra.loanPayments.length || extra.commissionIds.length) {
        await localApi.post("/payroll-extras/apply", {
          items: [{ payroll_id: created?.id || "", employee_id: form.employee_id, loan_payments: extra.loanPayments, commission_ids: extra.commissionIds }],
        }).catch(() => {});
      }
      toast.success("Planilla creada");
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const saveBatch = async () => {
    if (batchForm.items.length === 0) { toast.error("Selecciona al menos un empleado"); return; }
    setSaving(true);
    try {
      let autoMap = {};
      if (batchAutoOt || batchAutoLate) {
        try {
          const { data: calc } = await localApi.get("/fixed-schedule/calculate", { params: { start: batchForm.period_start, end: batchForm.period_end } });
          autoMap = Object.fromEntries((calc || []).map((r) => [r.employee_id, r]));
        } catch (err) {
          toast.error(apiError(err, "No se pudo calcular HE/tardanzas desde asistencia"));
        }
      }
      const { data: createdList } = await api.post("/payrolls/batch", {
        period_type: batchForm.period_type,
        period_start: batchForm.period_start,
        period_end: batchForm.period_end,
        apply_auto_deductions: batchForm.apply_auto_deductions,
        items: batchForm.items.map((it) => {
          const ded = batchExtras ? batchLoanDue(it) : 0;
          const bon = batchExtras ? batchCommTotal(it) : 0;
          return {
            employee_id: it.employee_id,
            days_worked: it.days_worked === "" ? null : Number(it.days_worked),
            overtime_hours: (Number(it.overtime_hours) || 0) + (batchAutoOt ? autoMap[it.employee_id]?.overtime_hours || 0 : 0),
            bonuses: (Number(it.bonuses) || 0) + bon,
            deductions: (Number(it.deductions) || 0) + ded + (batchAutoLate ? autoMap[it.employee_id]?.late_deduction || 0 : 0),
            apply_auto_deductions: batchForm.apply_auto_deductions,
            ...(ded > 0 ? { deduction_note: `Préstamo(s): ${fmtMoney(ded)}` } : {}),
            ...(bon > 0 ? { bonus_note: `Comisiones: ${fmtMoney(bon)}` } : {}),
          };
        }),
      });
      if (batchExtras && Array.isArray(createdList)) {
        const applyItems = createdList.map((p) => {
          const it = batchForm.items.find((x) => x.employee_id === p.employee_id);
          const lp = extras.loans.filter((l) => l.employee_id === p.employee_id)
            .map((l) => ({ loan_id: l.id, amount: loanDue(l, batchForm.period_type, it?.days_worked) }))
            .filter((x) => x.amount > 0);
          const cids = extras.commissions.filter((c) => c.employee_id === p.employee_id).map((c) => c.id);
          return { payroll_id: p.id, employee_id: p.employee_id, loan_payments: lp, commission_ids: cids };
        }).filter((x) => x.loan_payments.length || x.commission_ids.length);
        if (applyItems.length) {
          await localApi.post("/payroll-extras/apply", { items: applyItems }).catch(() => {});
        }
      }
      toast.success("Planillas generadas en lote");
      setBatchOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/payrolls/${toDelete.id}`);
      localApi.post("/payroll-extras/release", { payroll_id: toDelete.id }).catch(() => {});
      toast.success("Planilla eliminada");
      setToDelete(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const sendVouchers = async () => {
    if (selectedIds.length === 0) { toast.error("Selecciona al menos una planilla"); return; }
    setSendingVouchers(true);
    try {
      const { data } = await localApi.post("/payroll-tools/send-vouchers", { ids: targetIds });
      setVoucherResult(data);
      if (data.failed.length === 0) toast.success(`${data.sent} vouchers enviados por correo`);
      else toast.warning(`Enviados: ${data.sent}, fallidos: ${data.failed.length}`);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSendingVouchers(false);
      setToolMode(null);
    }
  };

  const downloadPresentation = async () => {
    setPdfLoading(true);
    try {
      const res = await localApi.post(
        "/payroll-tools/presentation-pdf",
        { ids: targetIds },
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `planilla_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Planilla PDF descargada");
    } catch (err) {
      toast.error(apiError(err, "No se pudo generar el PDF."));
    } finally {
      setPdfLoading(false);
      setToolMode(null);
    }
  };

  const openTool = (mode) => {
    setSelectedIds(payrolls.map((p) => p.id));
    setDateFrom("");
    setDateTo("");
    setToolMode(mode);
  };

  const toggleId = (id) => setSelectedIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const filteredPayrolls = payrolls
    ? payrolls.filter((p) => (!dateFrom || String(p.period_end).slice(0, 10) >= dateFrom) && (!dateTo || String(p.period_start).slice(0, 10) <= dateTo))
    : [];
  const filteredIds = filteredPayrolls.map((p) => p.id);
  const targetIds = selectedIds.filter((id) => filteredIds.includes(id));
  const allFilteredSelected = filteredIds.length > 0 && filteredIds.every((id) => selectedIds.includes(id));
  const toggleAllFiltered = () =>
    setSelectedIds((s) => (allFilteredSelected ? s.filter((id) => !filteredIds.includes(id)) : [...new Set([...s, ...filteredIds])]));
  const selectedPayrolls = filteredPayrolls.filter((p) => targetIds.includes(p.id));
  const withEmail = selectedPayrolls.filter((p) => employees.find((e) => e.id === p.employee_id && e.email)).length;

  const groupMap = {};
  (payrolls || []).forEach((p) => {
    const key = `${p.period_type}|${p.period_start}|${p.period_end}`;
    (groupMap[key] = groupMap[key] || { key, items: [], period_type: p.period_type, start: p.period_start, end: p.period_end }).items.push(p);
  });
  const groups = Object.values(groupMap).map((g) => ({ ...g, totalNet: g.items.reduce((s, p) => s + (p.net_pay || 0), 0) }));
  const toggleGroup = (key) => setCollapsed((c) => ({ ...c, [key]: !c[key] }));

  const totalDeds = (p) => p.deductions || 0;
  const otherDeds = (p) => Math.max(0, (p.deductions || 0) - (p.ihss || 0) - (p.rap || 0) - (p.isr || 0));
  const PERIOD_DAYS = { weekly: 7, biweekly: 15, monthly: 30 };
  const FREQ_LABELS = { daily: "diaria", weekly: "semanal", biweekly: "quincenal", monthly: "mensual" };
  const loanDue = (loan, periodType, daysWorked) => {
    const remaining = loan.remaining ?? ((loan.total || 0) - (loan.paid_amount || 0));
    if (remaining <= 0) return 0;
    let amt = 0;
    if (loan.frequency === "daily") amt = (loan.installment || 0) * (Number(daysWorked) || PERIOD_DAYS[periodType] || 15);
    else if (loan.frequency === periodType) amt = loan.installment || 0;
    return Math.min(amt, remaining);
  };
  const empLoans = extras.loans.filter((l) => l.employee_id === form.employee_id);
  const empComms = extras.commissions.filter((c) => c.employee_id === form.employee_id);
  const selectedExtras = () => {
    const loanPayments = [];
    let dedTotal = 0;
    empLoans.filter((l) => selLoans[l.id]).forEach((l) => {
      const amt = loanDue(l, form.period_type, form.days_worked);
      if (amt > 0) { loanPayments.push({ loan_id: l.id, amount: amt }); dedTotal += amt; }
    });
    const commissionIds = [];
    let bonTotal = 0;
    empComms.filter((c) => selComms[c.id]).forEach((c) => { commissionIds.push(c.id); bonTotal += c.amount || 0; });
    return { dedTotal, bonTotal, loanPayments, commissionIds };
  };
  const batchLoanDue = (it) => extras.loans.filter((l) => l.employee_id === it.employee_id).reduce((s, l) => s + loanDue(l, batchForm.period_type, it.days_worked), 0);
  const batchCommTotal = (it) => extras.commissions.filter((c) => c.employee_id === it.employee_id).reduce((s, c) => s + (c.amount || 0), 0);

  const openEdit = (p) => {
    setEditTarget(p);
    setEditForm({
      days_worked: p.days_worked ?? "",
      overtime_hours: p.overtime_hours || 0,
      bonuses: p.bonuses || 0,
      deductions: Math.round(otherDeds(p) * 100) / 100,
      apply_auto_deductions: !!((p.ihss || 0) + (p.rap || 0) + (p.isr || 0)),
      notes: p.notes || "",
    });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setEditSaving(true);
    try {
      await api.patch(`/payrolls/${editTarget.id}`, {
        days_worked: editForm.days_worked === "" ? null : Number(editForm.days_worked),
        overtime_hours: Number(editForm.overtime_hours) || 0,
        bonuses: Number(editForm.bonuses) || 0,
        deductions: Number(editForm.deductions) || 0,
        apply_auto_deductions: editForm.apply_auto_deductions,
        // CAUS no limpia las deducciones automáticas ya calculadas solo con el flag:
        // al desactivar hay que enviar los componentes en 0 explícitamente
        ...(editForm.apply_auto_deductions ? {} : { ihss: 0, rap: 0, isr: 0 }),
        notes: editForm.notes,
      });
      toast.success("Planilla corregida");
      setEditTarget(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setEditSaving(false);
    }
  };

  const resendVoucher = async (p) => {
    setResending(p.id);
    try {
      const { data } = await localApi.post("/payroll-tools/send-vouchers", { ids: [p.id] });
      if (data.sent > 0) toast.success(`Voucher reenviado a ${p.employee_name}`);
      else if (data.skipped_no_email?.length) toast.error(`${p.employee_name} no tiene correo registrado`);
      else toast.error("No se pudo enviar el voucher");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setResending("");
    }
  };

  useEffect(() => {
    setAutoData(null);
    if (!dialogOpen || !form.employee_id || !form.period_start || !form.period_end) return;
    let cancelled = false;
    setAutoCalcLoading(true);
    localApi.get("/fixed-schedule/calculate", { params: { start: form.period_start, end: form.period_end } })
      .then(({ data }) => {
        if (cancelled) return;
        const row = (data || []).find((r) => r.employee_id === form.employee_id);
        setAutoData(row && (row.overtime_hours > 0 || row.late_deduction > 0) ? row : null);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setAutoCalcLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogOpen, form.employee_id, form.period_start, form.period_end]);

  if (!payrolls) return <Loading label="Cargando planillas..." />;

  return (
    <div data-testid="planillas-page">
      <PageHeader title="Planillas" description="Genera y administra las planillas de pago con deducciones de ley" testid="planillas-header">
        <Button variant="outline" className="rounded-sm border-input bg-transparent text-foreground/80 hover:bg-accent hover:text-accent-foreground" data-testid="send-vouchers-button" onClick={() => openTool("vouchers")}>
          <PaperPlaneRight className="mr-2" size={15} /> Enviar vouchers
        </Button>
        <Button variant="outline" className="rounded-sm border-input bg-transparent text-foreground/80 hover:bg-accent hover:text-accent-foreground" data-testid="payroll-pdf-button" onClick={() => openTool("pdf")}>
          <FilePdf className="mr-2" size={15} /> PDF presentación
        </Button>
        <Button variant="outline" className="rounded-sm border-input bg-transparent text-foreground/80 hover:bg-accent hover:text-accent-foreground" data-testid="batch-payroll-button" onClick={openBatch}>
          <StackPlus className="mr-2" size={15} /> Generar en lote
        </Button>
        <Button className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/80" data-testid="new-payroll-button" onClick={openCreate}>
          <Plus className="mr-2" size={15} /> Nueva planilla
        </Button>
      </PageHeader>

      {payrolls.length === 0 ? (
        <EmptyState title="Sin planillas" description="Genera la primera planilla individual o en lote para todo el personal." />
      ) : (
        <div className="border border-border rounded-sm overflow-x-auto bg-card">
          <table className="w-full text-sm" data-testid="payrolls-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground border-b border-border">
                <th className="p-3 font-normal">Empleado</th>
                <th className="p-3 font-normal">Período</th>
                <th className="p-3 font-normal">Días</th>
                <th className="p-3 font-normal">Base</th>
                <th className="p-3 font-normal">Deducciones</th>
                <th className="p-3 font-normal">Neto</th>
                <th className="p-3 font-normal text-right">Acciones</th>
              </tr>
            </thead>
            {groups.map((g) => (
              <tbody key={g.key} className="divide-y divide-border" data-testid={`payroll-group-${g.key}`}>
                <tr className="bg-muted/40 cursor-pointer hover:bg-muted/70 transition-colors duration-200" onClick={() => toggleGroup(g.key)}>
                  <td colSpan={7} className="p-3">
                    <div className="flex items-center gap-3 flex-wrap">
                      {collapsed[g.key] ? <CaretRight size={14} className="text-muted-foreground" /> : <CaretDown size={14} className="text-muted-foreground" />}
                      <span className="font-medium text-foreground">{periodLabel(g.period_type)}</span>
                      <span className="text-xs text-muted-foreground">{fmtDate(g.start)} – {fmtDate(g.end)} · {g.items.length} planilla(s)</span>
                      <span className="ml-auto text-sm text-foreground font-medium">Neto total: {fmtMoney(g.totalNet)}</span>
                    </div>
                  </td>
                </tr>
                {!collapsed[g.key] && g.items.map((p) => (
                <tr key={p.id} className="hover:bg-muted/70 transition-colors duration-200" data-testid={`payroll-row-${p.employee_code}`}>
                  <td className="p-3">
                    <p className="text-foreground font-medium">{p.employee_name}</p>
                    <p className="text-xs text-muted-foreground">{p.employee_code}</p>
                  </td>
                  <td className="p-3 text-foreground/80">
                    {periodLabel(p.period_type)}
                    <span className="block text-xs text-muted-foreground">{fmtDate(p.period_start)} – {fmtDate(p.period_end)}</span>
                  </td>
                  <td className="p-3 text-foreground/80">{p.days_worked}</td>
                  <td className="p-3 text-foreground/80">{fmtMoney(p.base_pay)}</td>
                  <td className="p-3 text-red-400">{fmtMoney(totalDeds(p))}</td>
                  <td className="p-3 text-foreground font-medium">{fmtMoney(p.net_pay)}</td>
                  <td className="p-3 text-right">
                    <div className="inline-flex gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-accent" title="Ver detalle" data-testid={`payroll-view-${p.employee_code}`} onClick={() => setDetail(p)}>
                        <Eye size={15} />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-accent" title="Corregir planilla" data-testid={`payroll-edit-${p.employee_code}`} onClick={() => openEdit(p)}>
                        <PencilSimple size={15} />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-primary hover:bg-primary/10" title="Reenviar voucher solo a este empleado" disabled={resending === p.id} data-testid={`payroll-resend-${p.employee_code}`} onClick={() => resendVoucher(p)}>
                        {resending === p.id ? <CircleNotch className="animate-spin" size={15} /> : <PaperPlaneRight size={15} />}
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-red-400 hover:bg-red-950/30" title="Eliminar" data-testid={`payroll-delete-${p.employee_code}`} onClick={() => setToDelete(p)}>
                        <Trash size={15} />
                      </Button>
                    </div>
                  </td>
                </tr>
                ))}
              </tbody>
            ))}
          </table>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent aria-describedby={undefined} className="bg-card border-border rounded-sm sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-foreground">Nueva planilla</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="payroll-form">
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Empleado *</Label>
              <Select value={form.employee_id} onValueChange={(v) => {
                setForm({ ...form, employee_id: v });
                setPreview(null);
                setSelLoans(Object.fromEntries(extras.loans.filter((l) => l.employee_id === v).map((l) => [l.id, true])));
                setSelComms(Object.fromEntries(extras.commissions.filter((c) => c.employee_id === v).map((c) => [c.id, true])));
              }}>
                <SelectTrigger className={inputCls} data-testid="payroll-employee-select"><SelectValue placeholder="Seleccionar empleado" /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name} · {e.employee_code}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Tipo de período</Label>
              <Select value={form.period_type} onValueChange={onPeriodType}>
                <SelectTrigger className={inputCls} data-testid="payroll-period-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {PERIODS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Inicio</Label>
                <Input type="date" className={inputCls} data-testid="payroll-start" value={form.period_start} onChange={(e) => setForm({ ...form, period_start: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Fin</Label>
                <Input type="date" className={inputCls} data-testid="payroll-end" value={form.period_end} onChange={(e) => setForm({ ...form, period_end: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Días trabajados</Label>
              <Input type="number" step="0.5" min="0" className={inputCls} placeholder="Automático" value={form.days_worked} onChange={(e) => setForm({ ...form, days_worked: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Horas extra</Label>
              <Input type="number" step="0.5" min="0" className={inputCls} value={form.overtime_hours} onChange={(e) => setForm({ ...form, overtime_hours: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Bonificaciones (L)</Label>
              <Input type="number" step="0.01" min="0" className={inputCls} value={form.bonuses} onChange={(e) => setForm({ ...form, bonuses: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Otras deducciones (L)</Label>
              <Input type="number" step="0.01" min="0" className={inputCls} value={form.deductions} onChange={(e) => setForm({ ...form, deductions: e.target.value })} />
            </div>
            <div className="sm:col-span-2 flex items-center justify-between border border-border rounded-sm p-3">
              <div>
                <p className="text-sm text-zinc-200">Deducciones automáticas</p>
                <p className="text-xs text-muted-foreground">IHSS, RAP e ISR según configuración</p>
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={form.apply_auto_deductions} onCheckedChange={(v) => { setForm({ ...form, apply_auto_deductions: v }); setPreview(null); }} data-testid="auto-deductions-switch" />
                <Button type="button" variant="outline" size="sm" onClick={calcPreview} disabled={!form.apply_auto_deductions}
                  className="rounded-sm border-input bg-transparent text-foreground/80 hover:bg-accent hover:text-accent-foreground" data-testid="preview-button">
                  <Calculator className="mr-2" size={14} /> Vista previa
                </Button>
              </div>
            </div>
            {preview && (
              <div className="sm:col-span-2 grid grid-cols-4 gap-2" data-testid="preview-result">
                {[["IHSS", preview.ihss], ["RAP", preview.rap], ["ISR", preview.isr], ["Total", preview.total]].map(([l, v]) => (
                  <div key={l} className="border border-border rounded-sm p-3 text-center">
                    <p className="text-[11px] uppercase tracking-widest text-muted-foreground">{l}</p>
                    <p className="font-heading text-lg font-bold text-foreground mt-0.5">{fmtMoney(v)}</p>
                  </div>
                ))}
              </div>
            )}
            {form.employee_id && (empLoans.length > 0 || empComms.length > 0) && (
              <div className="sm:col-span-2 border border-border rounded-sm p-3 space-y-2" data-testid="payroll-extras-card">
                <p className="text-xs uppercase tracking-widest text-muted-foreground">Préstamos y comisiones pendientes</p>
                {empLoans.map((l) => {
                  const amt = loanDue(l, form.period_type, form.days_worked);
                  return (
                    <label key={l.id} className={`flex items-center gap-2 text-sm ${amt > 0 ? "text-foreground" : "text-muted-foreground"}`}>
                      <Checkbox checked={amt > 0 && !!selLoans[l.id]} disabled={amt <= 0} onCheckedChange={(c) => setSelLoans({ ...selLoans, [l.id]: !!c })} className="border-input" data-testid={`extra-loan-${l.id}`} />
                      <span>Cuota de préstamo ({FREQ_LABELS[l.frequency]}) · resta {fmtMoney(l.remaining ?? l.total)}</span>
                      <span className="ml-auto text-red-400">{amt > 0 ? `- ${fmtMoney(amt)}` : "no aplica en este período"}</span>
                    </label>
                  );
                })}
                {empComms.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 text-sm text-foreground">
                    <Checkbox checked={!!selComms[c.id]} onCheckedChange={(v) => setSelComms({ ...selComms, [c.id]: !!v })} className="border-input" data-testid={`extra-comm-${c.id}`} />
                    <span>Comisión: {c.concept}</span>
                    <span className="ml-auto text-emerald-500">+ {fmtMoney(c.amount)}</span>
                  </label>
                ))}
              </div>
            )}
            {autoCalcLoading && (
              <p className="sm:col-span-2 text-xs text-muted-foreground flex items-center gap-2" data-testid="payroll-autocalc-loading">
                <CircleNotch className="animate-spin" size={13} /> Revisando asistencia del período...
              </p>
            )}
            {autoData && (
              <div className="sm:col-span-2 border border-primary/30 bg-primary/5 rounded-sm p-3 space-y-2" data-testid="payroll-autocalc-panel">
                <p className="text-xs uppercase tracking-widest text-primary">Detectado desde asistencia — tú decides si aplica</p>
                <label className="flex items-center justify-between gap-3 text-sm text-foreground cursor-pointer">
                  <span>Horas extra detectadas: <b>{autoData.overtime_hours}h</b></span>
                  <Switch checked={applyOt} onCheckedChange={setApplyOt} data-testid="apply-ot-switch" />
                </label>
                <label className="flex items-center justify-between gap-3 text-sm text-foreground cursor-pointer">
                  <span>Tardanzas: <b>{autoData.late_hours}h</b> (− {fmtMoney(autoData.late_deduction)})</span>
                  <Switch checked={applyLate} onCheckedChange={setApplyLate} data-testid="apply-late-switch" />
                </label>
              </div>
            )}
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Notas</Label>
              <Textarea className={`${inputCls} min-h-[70px]`} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <DialogFooter className="sm:col-span-2">
              <Button type="submit" disabled={saving} className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/80" data-testid="payroll-save-button">
                {saving && <CircleNotch className="animate-spin mr-2" size={15} />} Crear planilla
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editTarget} onOpenChange={() => setEditTarget(null)}>
        <DialogContent aria-describedby={undefined} className="bg-card border-border rounded-sm sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-foreground">Corregir planilla — {editTarget?.employee_name}</DialogTitle>
          </DialogHeader>
          {editTarget && (
            <form onSubmit={saveEdit} className="space-y-4" data-testid="payroll-edit-form">
              <p className="text-xs text-muted-foreground">{periodLabel(editTarget.period_type)} · {fmtDate(editTarget.period_start)} – {fmtDate(editTarget.period_end)}</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Días trabajados</Label>
                  <Input type="number" step="0.5" min="0" className={inputCls} data-testid="edit-days" value={editForm.days_worked} onChange={(e) => setEditForm({ ...editForm, days_worked: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Horas extra</Label>
                  <Input type="number" step="0.5" min="0" className={inputCls} data-testid="edit-overtime" value={editForm.overtime_hours} onChange={(e) => setEditForm({ ...editForm, overtime_hours: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Bonificaciones (L)</Label>
                  <Input type="number" step="0.01" min="0" className={inputCls} data-testid="edit-bonuses" value={editForm.bonuses} onChange={(e) => setEditForm({ ...editForm, bonuses: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Otras deducciones (L)</Label>
                  <Input type="number" step="0.01" min="0" className={inputCls} data-testid="edit-deductions" value={editForm.deductions} onChange={(e) => setEditForm({ ...editForm, deductions: e.target.value })} />
                </div>
              </div>
              <div className="flex items-center justify-between border border-border rounded-sm p-3">
                <div>
                  <p className="text-sm text-foreground">Deducciones automáticas</p>
                  <p className="text-xs text-muted-foreground">IHSS, RAP e ISR se recalculan al guardar</p>
                </div>
                <Switch checked={!!editForm.apply_auto_deductions} onCheckedChange={(v) => setEditForm({ ...editForm, apply_auto_deductions: v })} data-testid="edit-auto-deductions" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Notas</Label>
                <Textarea className={`${inputCls} min-h-[60px]`} value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} />
              </div>
              <p className="text-[11px] text-amber-500/80">Si esta planilla tenía cuotas de préstamo o comisiones aplicadas, sus saldos no se ajustan al corregir los montos aquí.</p>
              <DialogFooter>
                <Button type="submit" disabled={editSaving} className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/80" data-testid="payroll-edit-save">
                  {editSaving && <CircleNotch className="animate-spin mr-2" size={15} />} Guardar corrección
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={batchOpen} onOpenChange={setBatchOpen}>
        <DialogContent aria-describedby={undefined} className="bg-card border-border rounded-sm sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-foreground">Planillas en lote</DialogTitle>
          </DialogHeader>
          <div className="space-y-4" data-testid="batch-form">
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Período</Label>
                <Select value={batchForm.period_type} onValueChange={(v) => setBatchForm({ ...batchForm, period_type: v, ...suggestPeriod(v) })}>
                  <SelectTrigger className={inputCls} data-testid="batch-period-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {PERIODS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Inicio</Label>
                <Input type="date" className={inputCls} data-testid="batch-start" value={batchForm.period_start} onChange={(e) => setBatchForm({ ...batchForm, period_start: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Fin</Label>
                <Input type="date" className={inputCls} data-testid="batch-end" value={batchForm.period_end} onChange={(e) => setBatchForm({ ...batchForm, period_end: e.target.value })} />
              </div>
            </div>
            <div className="flex items-center justify-between border border-border rounded-sm p-3">
              <div>
                <p className="text-sm text-foreground">Deducciones automáticas</p>
                <p className="text-xs text-muted-foreground">IHSS, RAP e ISR según configuración — aplica a todo el lote</p>
              </div>
              <Switch checked={batchForm.apply_auto_deductions} onCheckedChange={(v) => setBatchForm({ ...batchForm, apply_auto_deductions: v })} data-testid="batch-auto-deductions-switch" />
            </div>
            <div className="border border-border rounded-sm p-3 space-y-2.5">
              <p className="text-sm text-foreground">Calcular desde asistencia (horario fijo + marcaciones)</p>
              <label className="flex items-center justify-between gap-3 text-sm text-foreground/80 cursor-pointer">
                <span>Aplicar horas extra detectadas</span>
                <Switch checked={batchAutoOt} onCheckedChange={setBatchAutoOt} data-testid="batch-auto-ot-switch" />
              </label>
              <label className="flex items-center justify-between gap-3 text-sm text-foreground/80 cursor-pointer">
                <span>Descontar tardanzas detectadas</span>
                <Switch checked={batchAutoLate} onCheckedChange={setBatchAutoLate} data-testid="batch-auto-late-switch" />
              </label>
            </div>
            <div className="flex items-center justify-between border border-border rounded-sm p-3">
              <div>
                <p className="text-sm text-foreground">Incluir préstamos y comisiones</p>
                <p className="text-xs text-muted-foreground">Suma cuotas de préstamos y comisiones pendientes de cada empleado seleccionado</p>
              </div>
              <Switch checked={batchExtras} onCheckedChange={setBatchExtras} data-testid="batch-extras-switch" />
            </div>
            <div className="border border-border rounded-sm divide-y divide-border max-h-80 overflow-y-auto">
              {employees.map((e) => {
                const item = batchForm.items.find((x) => x.employee_id === e.id);
                return (
                  <div key={e.id} className="p-3 hover:bg-muted/70 transition-colors duration-200">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <Checkbox
                        checked={!!item}
                        onCheckedChange={(c) =>
                          setBatchForm((f) => ({
                            ...f,
                            items: c
                              ? [...f.items, { employee_id: e.id, days_worked: "", overtime_hours: "", bonuses: "", deductions: "" }]
                              : f.items.filter((x) => x.employee_id !== e.id),
                          }))
                        }
                        className="border-input"
                        data-testid={`batch-check-${e.employee_code}`}
                      />
                      <span className="text-sm text-foreground">{e.full_name}</span>
                      <span className="text-xs text-muted-foreground ml-auto">{fmtMoney(e.monthly_salary)}</span>
                    </label>
                    {item && (
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2 pl-7" data-testid={`batch-fields-${e.employee_code}`}>
                        {[
                          ["days_worked", "Días trab.", "Auto"],
                          ["overtime_hours", "Horas extra", "0"],
                          ["bonuses", "Bonif. (L)", "0"],
                          ["deductions", "Otras ded. (L)", "0"],
                        ].map(([k, label, ph]) => (
                          <div key={k}>
                            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</Label>
                            <Input
                              type="number"
                              step="0.5"
                              min="0"
                              className={`${inputCls} h-8 text-xs mt-1`}
                              placeholder={ph}
                              value={item[k]}
                              data-testid={`batch-${k}-${e.employee_code}`}
                              onChange={(ev) =>
                                setBatchForm((f) => ({
                                  ...f,
                                  items: f.items.map((x) => (x.employee_id === e.id ? { ...x, [k]: ev.target.value } : x)),
                                }))
                              }
                            />
                          </div>
                        ))}
                      </div>
                    )}
                    {item && batchExtras && (batchLoanDue(item) > 0 || batchCommTotal(item) > 0) && (
                      <p className="text-[11px] text-muted-foreground mt-1.5 pl-7" data-testid={`batch-extras-hint-${e.employee_code}`}>
                        {batchLoanDue(item) > 0 && `Préstamo: - ${fmtMoney(batchLoanDue(item))}`}
                        {batchLoanDue(item) > 0 && batchCommTotal(item) > 0 && " · "}
                        {batchCommTotal(item) > 0 && `Comisiones: + ${fmtMoney(batchCommTotal(item))}`}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground">
              Edita los valores de cada empleado seleccionado.{" "}
              {batchForm.apply_auto_deductions
                ? "Las deducciones automáticas (IHSS, RAP, ISR) se aplican a todos."
                : "Las deducciones automáticas (IHSS, RAP, ISR) están desactivadas para este lote."}
            </p>
            <DialogFooter>
              <Button onClick={saveBatch} disabled={saving} className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/80" data-testid="batch-save-button">
                {saving && <CircleNotch className="animate-spin mr-2" size={15} />}
                Generar {batchForm.items.length > 0 ? `(${batchForm.items.length})` : ""}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent aria-describedby={undefined} className="bg-card border-border rounded-sm sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-foreground">Detalle de planilla</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4" data-testid="payroll-detail">
              <div>
                <p className="text-foreground font-medium">{detail.employee_name}</p>
                <p className="text-xs text-muted-foreground">{periodLabel(detail.period_type)} · {fmtDate(detail.period_start)} – {fmtDate(detail.period_end)}</p>
              </div>
              <div className="border border-border rounded-sm divide-y divide-border text-sm">
                {[
                  ["Salario mensual", detail.monthly_salary],
                  ["Salario del período", detail.period_type === "biweekly" ? detail.biweekly_salary : detail.monthly_salary],
                  [`Días trabajados (${detail.days_worked})`, detail.base_pay],
                  [`Horas extra (${detail.overtime_hours}h)`, detail.overtime_pay],
                  ["Bonificaciones", detail.bonuses],
                ].map(([l, v]) => (
                  <div key={l} className="flex justify-between p-3"><span className="text-muted-foreground">{l}</span><span className="text-foreground">{fmtMoney(v)}</span></div>
                ))}
                {[
                  ["IHSS", detail.ihss], ["RAP", detail.rap], ["ISR", detail.isr],
                  ["Otras deducciones", otherDeds(detail)],
                ].map(([l, v]) => (
                  <div key={l} className="flex justify-between p-3"><span className="text-muted-foreground">{l}</span><span className="text-red-400">- {fmtMoney(v)}</span></div>
                ))}
                <div className="flex justify-between p-3 bg-muted">
                  <span className="text-foreground font-medium">Pago neto</span>
                  <span className="font-heading text-lg font-bold text-foreground" data-testid="detail-net-pay">{fmtMoney(detail.net_pay)}</span>
                </div>
              </div>
              {detail.notes && <p className="text-xs text-muted-foreground">Notas: {detail.notes}</p>}
              {(detail.deduction_note || detail.bonus_note) && (
                <p className="text-xs text-muted-foreground" data-testid="detail-extra-notes">
                  {detail.deduction_note && <span>Deducciones: {detail.deduction_note} </span>}
                  {detail.bonus_note && <span>· Bonificaciones: {detail.bonus_note}</span>}
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!toolMode} onOpenChange={() => setToolMode(null)}>
        <DialogContent aria-describedby={undefined} className="bg-card border-border rounded-sm sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-foreground">
              {toolMode === "vouchers" ? "Enviar vouchers por correo" : "Descargar planilla en PDF"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3" data-testid="payroll-select-dialog">
            <p className="text-sm text-muted-foreground">
              {toolMode === "vouchers"
                ? "Selecciona las planillas cuyos comprobantes se enviarán al correo registrado de cada empleado."
                : "Selecciona las planillas que se incluirán en el PDF de presentación."}
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Desde</Label>
                <Input type="date" className={inputCls} data-testid="filter-date-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Hasta</Label>
                <Input type="date" className={inputCls} data-testid="filter-date-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <button type="button" className="text-xs text-muted-foreground hover:text-foreground transition-colors duration-200" data-testid="select-all-toggle"
                onClick={toggleAllFiltered}>
                {allFilteredSelected ? "Quitar todas" : "Seleccionar todas"}
              </button>
              <span className="text-xs text-muted-foreground" data-testid="selection-counter">{targetIds.length} de {filteredPayrolls.length}</span>
            </div>
            <div className="border border-border rounded-sm divide-y divide-border max-h-72 overflow-y-auto">
              {filteredPayrolls.map((p) => {
                const hasEmail = !!employees.find((e) => e.id === p.employee_id && e.email);
                return (
                  <label key={p.id} className="flex items-center gap-3 p-3 cursor-pointer hover:bg-muted/70 transition-colors duration-200" data-testid={`select-payroll-${p.employee_code}`}>
                    <Checkbox checked={selectedIds.includes(p.id)} onCheckedChange={() => toggleId(p.id)} className="border-input" data-testid={`select-check-${p.employee_code}`} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-foreground truncate">{p.employee_name}</p>
                      <p className="text-xs text-muted-foreground">{periodLabel(p.period_type)} · {fmtDate(p.period_start)} – {fmtDate(p.period_end)}</p>
                    </div>
                    {toolMode === "vouchers" && !hasEmail && (
                      <span className="text-[11px] text-amber-500/90 border border-amber-900/50 rounded-sm px-1.5 py-0.5">sin correo</span>
                    )}
                    <span className="text-sm text-foreground font-medium shrink-0">{fmtMoney(p.net_pay)}</span>
                  </label>
                );
              })}
            </div>
            {toolMode === "vouchers" && withEmail < targetIds.length && (
              <p className="text-xs text-amber-500/90 border border-amber-900/50 bg-amber-950/30 rounded-sm px-3 py-2">
                {targetIds.length - withEmail} registro(s) sin correo registrado serán omitidos.
              </p>
            )}
            <DialogFooter>
              {toolMode === "vouchers" ? (
                <Button onClick={sendVouchers} disabled={sendingVouchers || targetIds.length === 0} className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/80" data-testid="vouchers-send-confirm">
                  {sendingVouchers && <CircleNotch className="animate-spin mr-2" size={15} />} Enviar {targetIds.length} vouchers
                </Button>
              ) : (
                <Button onClick={downloadPresentation} disabled={pdfLoading || targetIds.length === 0} className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/80" data-testid="pdf-download-confirm">
                  {pdfLoading && <CircleNotch className="animate-spin mr-2" size={15} />} Descargar PDF ({targetIds.length})
                </Button>
              )}
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!voucherResult} onOpenChange={() => setVoucherResult(null)}>
        <DialogContent aria-describedby={undefined} className="bg-card border-border rounded-sm sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-foreground">Resultado del envío</DialogTitle>
          </DialogHeader>
          {voucherResult && (
            <div className="space-y-3" data-testid="vouchers-result">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="border border-border rounded-sm p-3">
                  <p className="font-heading text-2xl font-bold text-emerald-500">{voucherResult.sent}</p>
                  <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Enviados</p>
                </div>
                <div className="border border-border rounded-sm p-3">
                  <p className="font-heading text-2xl font-bold text-red-400">{voucherResult.failed.length}</p>
                  <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Fallidos</p>
                </div>
                <div className="border border-border rounded-sm p-3">
                  <p className="font-heading text-2xl font-bold text-muted-foreground">{voucherResult.skipped_no_email.length}</p>
                  <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Sin correo</p>
                </div>
              </div>
              {voucherResult.skipped_no_email.length > 0 && (
                <p className="text-xs text-muted-foreground">Sin correo: {voucherResult.skipped_no_email.join(", ")}</p>
              )}
              {voucherResult.failed.length > 0 && (
                <p className="text-xs text-red-400">Fallidos: {voucherResult.failed.join(", ")}</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!toDelete} onOpenChange={() => setToDelete(null)}
        title="Eliminar planilla" description={`Se eliminará la planilla de ${toDelete?.employee_name} (${fmtDate(toDelete?.period_start)} – ${fmtDate(toDelete?.period_end)}).`}
        onConfirm={remove} testid="delete-payroll" />
    </div>
  );
}
